import React, { useEffect, useState } from 'react';
import { Box, Switch, FormGroup, FormControlLabel, Typography, Alert, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../contexts/NotificationContext';

export const NotificationPreferences: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { preferences, updatePreferences, isLoading } = useNotifications();
  const [notificationsSupported, setNotificationsSupported] = useState(true);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');

  useEffect(() => {
    const checkNotificationSupport = () => {
      const isSupported = 'Notification' in window;
      setNotificationsSupported(isSupported);
      if (isSupported) {
        setPermissionStatus(Notification.permission);
      }
    };

    checkNotificationSupport();
  }, []);

  const handlePreferenceChange = async (key: keyof typeof preferences) => {
    const newPreferences = { ...preferences, [key]: !preferences[key] };
    await updatePreferences(newPreferences);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!notificationsSupported) {
    return (
      <Alert severity="warning">
        {t('common.notifications.browserNotSupported')}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('common.notifications.title')}
      </Typography>

      {permissionStatus === 'denied' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('common.notifications.blocked')}
        </Alert>
      )}

      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={preferences.enabled}
              onChange={() => handlePreferenceChange('enabled')}
            />
          }
          label={t('common.notifications.enable')}
        />

        {preferences.enabled && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.maintenance}
                  onChange={() => handlePreferenceChange('maintenance')}
                />
              }
              label={t('common.notifications.maintenance')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.fuel}
                  onChange={() => handlePreferenceChange('fuel')}
                />
              }
              label={t('common.notifications.fuel')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.mileage}
                  onChange={() => handlePreferenceChange('mileage')}
                />
              }
              label={t('common.notifications.mileage')}
            />
          </>
        )}
      </FormGroup>
    </Box>
  );
};
